import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { ChevronLeft, ChevronRight, Calendar, Printer, CheckCircle2, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';

import { useStore } from '../context/StoreContext';
const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

function getMonday(date) {
  const d = new Date(date);
  const day = d.getDay();
  const diff = day === 0 ? -6 : 1 - day;
  d.setDate(d.getDate() + diff);
  d.setHours(0, 0, 0, 0);
  return d;
}
const isoDate = (d) => {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${dd}`;
};
const addDays = (d, n) => { const r = new Date(d); r.setDate(r.getDate() + n); return r; };

export default function TaskAccountability() {
  const { storeName } = useStore();
  const { getAuthHeader } = useAuth();
  const [weekStart, setWeekStart] = useState(() => isoDate(getMonday(new Date())));
  const [storeFilter, setStoreFilter] = useState(''); // '' = all
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const monday = new Date(weekStart + 'T00:00:00');

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const url = `${API_URL}/reports/task-accountability?week_start=${weekStart}${storeFilter ? `&store=${storeFilter}` : ''}`;
      const res = await axios.get(url, { headers: getAuthHeader() });
      setData(res.data);
    } catch (e) {
      toast.error('Failed to load report');
    } finally {
      setLoading(false);
    }
  }, [weekStart, storeFilter, getAuthHeader]);

  useEffect(() => { refresh(); }, [refresh]);

  const shiftWeek = useCallback((delta) => setWeekStart(isoDate(addDays(monday, delta * 7))), [monday]);
  const goThisWeek = useCallback(() => setWeekStart(isoDate(getMonday(new Date()))), []);

  const weekLabel = `${monday.toLocaleDateString('en-NZ', { day: '2-digit', month: 'short' })} – ${addDays(monday, 6).toLocaleDateString('en-NZ', { day: '2-digit', month: 'short', year: 'numeric' })}`;

  const scoreColor = (missed, total) => {
    if (total === 0) return 'text-slate-400';
    if (missed === 0) return 'text-emerald-700';
    if (missed >= Math.ceil(total / 2)) return 'text-red-700';
    return 'text-amber-700';
  };

  const scoreBg = (missed, total) => {
    if (total === 0) return 'bg-slate-100';
    if (missed === 0) return 'bg-emerald-50 border-emerald-200';
    if (missed >= Math.ceil(total / 2)) return 'bg-red-50 border-red-200';
    return 'bg-amber-50 border-amber-200';
  };

  return (
    <div className="space-y-4 max-w-[1400px]" data-testid="accountability-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-3xl font-bold tracking-tight text-[#1b5e20]">Task Accountability</h1>
          <p className="text-sm text-slate-500">Weekly view of which staff completed which task categories on their shifts.</p>
        </div>
        <div className="flex items-center gap-2">
          <div className="flex rounded-md border border-slate-200 overflow-hidden">
            <button
              onClick={() => setStoreFilter('')}
              className={`px-3 py-1.5 text-sm font-medium ${storeFilter === '' ? 'bg-[#1b5e20] text-white' : 'bg-white text-slate-700 hover:bg-slate-50'}`}
              data-testid="store-all"
            >All stores</button>
            <button
              onClick={() => setStoreFilter('orewa')}
              className={`px-3 py-1.5 text-sm font-medium border-l border-slate-200 ${storeFilter === 'orewa' ? 'bg-[#1b5e20] text-white' : 'bg-white text-slate-700 hover:bg-slate-50'}`}
              data-testid="store-orewa"
            >{storeName('orewa')}</button>
            <button
              onClick={() => setStoreFilter('eastridge')}
              className={`px-3 py-1.5 text-sm font-medium border-l border-slate-200 ${storeFilter === 'eastridge' ? 'bg-[#1b5e20] text-white' : 'bg-white text-slate-700 hover:bg-slate-50'}`}
              data-testid="store-eastridge"
            >{storeName('eastridge')}</button>
          </div>
          <Button variant="outline" onClick={() => shiftWeek(-1)} data-testid="prev-week"><ChevronLeft className="w-4 h-4" /></Button>
          <Button variant="outline" onClick={goThisWeek} data-testid="this-week" className="font-semibold"><Calendar className="w-4 h-4 mr-2" /> This week</Button>
          <Button variant="outline" onClick={() => shiftWeek(1)} data-testid="next-week"><ChevronRight className="w-4 h-4" /></Button>
          <Button variant="outline" onClick={() => window.print()} data-testid="print-report"><Printer className="w-4 h-4 mr-2" /> Print</Button>
        </div>
      </div>

      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-lg" data-testid="week-label">Week of {weekLabel}</CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <p className="text-center text-slate-500 py-8">Loading...</p>
          ) : !data || data.staff.length === 0 ? (
            <div className="text-center py-12 space-y-3" data-testid="accountability-empty">
              <p className="text-slate-500 mb-2">No staff with logged shifts for this week.</p>
              <p className="text-xs text-slate-400">Make sure staff have entered their hours in the Weekly Roster first.</p>
              {data?.diagnostics && (
                <div className="inline-block text-left text-xs bg-slate-50 border border-slate-200 rounded-lg p-3 mt-3" data-testid="accountability-diagnostics">
                  <p className="font-semibold text-slate-700 mb-1">Why is this empty?</p>
                  <ul className="space-y-0.5 text-slate-600">
                    <li>• Roster rows for this week: <span className="font-mono font-semibold">{data.diagnostics.hours_rows_for_week}</span></li>
                    <li>• Rows with actual shifts (matching <span className="font-semibold">{storeFilter || 'any store'}</span>): <span className="font-mono font-semibold">{data.diagnostics.hours_rows_with_shifts_for_filter}</span></li>
                    <li>• Tasks scheduled this week (matching filter): <span className="font-mono font-semibold">{data.diagnostics.tasks_for_week_total}</span></li>
                    <li>• Tasks completed: <span className="font-mono font-semibold">{data.diagnostics.tasks_completed_total}</span></li>
                  </ul>
                  <p className="mt-2 text-[11px] text-slate-500">
                    {data.diagnostics.hours_rows_for_week === 0
                      ? '→ No roster has been entered for this week at all. Open Weekly Roster to set hours.'
                      : data.diagnostics.hours_rows_with_shifts_for_filter === 0
                        ? '→ Roster rows exist but none have shift times for the selected store. Try a different store filter or fill in start/end times on Weekly Roster.'
                        : data.diagnostics.tasks_for_week_total === 0
                          ? '→ No staff tasks were created/seeded for this week+store. Tasks auto-seed when someone opens Daily Tasks with that store selected.'
                          : '→ Tasks and roster exist but no completions yet — staff need to tick off their tasks on Daily Tasks.'}
                  </p>
                </div>
              )}
            </div>
          ) : (
            <div className="space-y-6" data-testid="accountability-list">
              {data.staff.map(staff => {
                const totalMissed = staff.by_category.reduce((s, c) => s + c.shifts_missed, 0);
                const totalShiftTasks = staff.by_category.reduce((s, c) => s + c.shifts_with_task, 0);
                const totalCompleted = staff.by_category.reduce((s, c) => s + c.shifts_completed, 0);
                const completionPct = totalShiftTasks > 0 ? Math.round((totalCompleted / totalShiftTasks) * 100) : 0;
                return (
                  <div key={staff.staff_name} className="border border-slate-200 rounded-lg overflow-hidden" data-testid={`staff-${staff.staff_name}`}>
                    <div className="flex items-center justify-between px-4 py-3 bg-slate-50 border-b border-slate-200">
                      <div>
                        <h3 className="text-lg font-bold text-slate-800">{staff.staff_name}</h3>
                        <p className="text-xs text-slate-500">Worked {staff.shifts_worked} {staff.shifts_worked === 1 ? 'shift' : 'shifts'} this week</p>
                      </div>
                      <div className="text-right">
                        <p className="text-xs uppercase text-slate-500">Overall completion</p>
                        <p className={`text-2xl font-bold ${completionPct >= 90 ? 'text-emerald-700' : completionPct >= 70 ? 'text-amber-700' : 'text-red-700'}`}>
                          {completionPct}%
                        </p>
                        {totalMissed > 0 && <p className="text-xs text-red-600">{totalMissed} tasks missed</p>}
                      </div>
                    </div>
                    <div className="p-4 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                      {staff.by_category.length === 0 ? (
                        <p className="text-sm text-slate-400 col-span-3">No category tasks ran while this staff was on shift.</p>
                      ) : staff.by_category.map(c => (
                        <div key={c.category}
                          className={`rounded-lg border p-3 ${scoreBg(c.shifts_missed, c.shifts_with_task)}`}
                          data-testid={`cat-${staff.staff_name}-${c.category}`}>
                          <div className="flex items-start justify-between gap-2">
                            <p className="text-sm font-semibold text-slate-800 leading-tight">{c.label}</p>
                            {c.shifts_missed === 0 && c.shifts_with_task > 0 ? (
                              <CheckCircle2 className="w-4 h-4 text-emerald-600 flex-shrink-0" />
                            ) : c.shifts_missed > 0 ? (
                              <AlertCircle className="w-4 h-4 text-red-500 flex-shrink-0" />
                            ) : null}
                          </div>
                          <p className={`text-2xl font-bold mt-1 ${scoreColor(c.shifts_missed, c.shifts_with_task)}`}>
                            {c.shifts_completed}/{c.shifts_with_task}
                          </p>
                          <p className="text-xs text-slate-500 mt-0.5">
                            {c.shifts_with_task === 0
                              ? 'No tasks this week'
                              : c.shifts_missed === 0
                                ? 'All done ✓'
                                : `Missed ${c.shifts_missed} of ${c.shifts_with_task} shifts`}
                          </p>
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      <p className="text-xs text-slate-400">
        💡 Each card shows completed / shifts this category had tasks on. Missed shifts = the staff worked that day, the task existed, but no one completed it (or they did not tick their name on it).
      </p>
    </div>
  );
}
